import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import type { ProfileData, EditableTabProps } from "../../../lib/profileTypes";
import { SectionCard } from "../card/SectionCard";

interface EducationTabProps extends EditableTabProps {
  profile: ProfileData;
  candidateId: number;
  hasResume: boolean;
}

export function EducationTab({
  profile,
  candidateId,
  hasResume,
  editing,
  onEdit,
  onSave,
  onCancel,
  saving,
}: EducationTabProps) {
  const queryClient = useQueryClient();
  const [edu, setEdu] = useState(profile.education_history || []);
  const [work, setWork] = useState(profile.work_experience || []);

  const extract = useMutation({
    mutationFn: () => api.extractCandidateEduWork(candidateId),
    onSuccess: (data) => {
      setEdu(data.education_history || []);
      setWork(data.work_experience || []);
      queryClient.invalidateQueries({
        queryKey: queryKeys.candidateProfile(candidateId),
      });
    },
  });

  const empty = edu.length === 0 && work.length === 0;
  useEffect(() => {
    if (
      !editing &&
      empty &&
      hasResume &&
      !extract.isPending &&
      !extract.isSuccess &&
      !extract.isError
    ) {
      extract.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, empty, hasResume]);

  if (!editing) {
    return (
      <div className="space-y-3">
        {empty && extract.isPending && (
          <div className="flex items-center gap-2 rounded-card border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
            <svg
              className="h-3.5 w-3.5 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
                opacity="0.25"
              />
              <path
                fill="currentColor"
                d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            Auto-extracting education & work history from the resume…
          </div>
        )}
        {empty && extract.isError && (
          <div className="flex items-center justify-between rounded-card border border-rose-200 bg-rose-50 px-4 py-2 text-xs text-error-text">
            <span>Couldn't extract from the resume automatically.</span>
            <button
              onClick={() => extract.mutate()}
              className="rounded-btn bg-near-black px-3 py-1 text-xs font-medium text-white hover:bg-rausch"
            >
              Retry
            </button>
          </div>
        )}
        {empty && !hasResume && !extract.isPending && (
          <div className="rounded-card border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
            No resume on file for this candidate. Re-upload the resume to
            auto-populate education & work history.
          </div>
        )}
        <SectionCard
          title="Education"
          onEdit={onEdit}
          extra={
            hasResume && !empty ? (
              <button
                onClick={() => extract.mutate()}
                disabled={extract.isPending}
                className="text-[10px] font-medium text-rausch hover:underline disabled:opacity-50"
              >
                {extract.isPending
                  ? "Re-extracting…"
                  : "Re-extract from resume"}
              </button>
            ) : null
          }
        >
          {edu.length === 0 ? (
            <p className="text-xs text-text-secondary">No education records</p>
          ) : (
            edu.map((e, i) => (
              <div
                key={i}
                className="border-b border-surface-secondary py-2 last:border-0"
              >
                <p className="text-xs font-semibold text-near-black">
                  {e.degree}
                </p>
                <p className="text-xs text-text-secondary">
                  {e.school} · {e.year}
                </p>
              </div>
            ))
          )}
        </SectionCard>
        <SectionCard title="Work Experience" onEdit={onEdit}>
          {work.length === 0 ? (
            <p className="text-xs text-text-secondary">No work records</p>
          ) : (
            work.map((w, i) => (
              <div
                key={i}
                className="border-b border-surface-secondary py-2 last:border-0"
              >
                <p className="text-xs font-semibold text-near-black">
                  {w.title}
                </p>
                <p className="text-xs text-text-secondary">
                  {w.company} · {w.from} – {w.to || "Present"}
                </p>
                {w.description && (
                  <p className="mt-0.5 text-xs text-text-secondary">
                    {w.description}
                  </p>
                )}
              </div>
            ))
          )}
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SectionCard title="Education" editing>
        {edu.map((e, i) => (
          <div
            key={i}
            className="mb-2 grid grid-cols-3 gap-2 border-b border-surface-secondary pb-2"
          >
            <input
              value={e.degree}
              onChange={(ev) => {
                const n = [...edu];
                n[i] = { ...e, degree: ev.target.value };
                setEdu(n);
              }}
              placeholder="Degree"
              className="rounded-btn border border-border-card bg-white px-2 py-1 text-xs"
            />
            <input
              value={e.school}
              onChange={(ev) => {
                const n = [...edu];
                n[i] = { ...e, school: ev.target.value };
                setEdu(n);
              }}
              placeholder="School"
              className="rounded-btn border border-border-card bg-white px-2 py-1 text-xs"
            />
            <div className="flex gap-1">
              <input
                value={e.year}
                onChange={(ev) => {
                  const n = [...edu];
                  n[i] = { ...e, year: ev.target.value };
                  setEdu(n);
                }}
                placeholder="Year"
                className="flex-1 rounded-btn border border-border-card bg-white px-2 py-1 text-xs"
              />
              <button
                type="button"
                onClick={() => setEdu(edu.filter((_, j) => j !== i))}
                className="text-xs text-error-text"
              >
                x
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setEdu([...edu, { school: "", degree: "", year: "" }])}
          className="text-xs font-medium text-rausch"
        >
          + Add education
        </button>
      </SectionCard>
      <SectionCard title="Work Experience" editing>
        {work.map((w, i) => (
          <div
            key={i}
            className="mb-2 space-y-1 border-b border-surface-secondary pb-2"
          >
            <div className="grid grid-cols-2 gap-2">
              <input
                value={w.title}
                onChange={(ev) => {
                  const n = [...work];
                  n[i] = { ...w, title: ev.target.value };
                  setWork(n);
                }}
                placeholder="Title"
                className="rounded-btn border border-border-card bg-white px-2 py-1 text-xs"
              />
              <input
                value={w.company}
                onChange={(ev) => {
                  const n = [...work];
                  n[i] = { ...w, company: ev.target.value };
                  setWork(n);
                }}
                placeholder="Company"
                className="rounded-btn border border-border-card bg-white px-2 py-1 text-xs"
              />
            </div>
            <div className="flex gap-2">
              <input
                value={w.from}
                onChange={(ev) => {
                  const n = [...work];
                  n[i] = { ...w, from: ev.target.value };
                  setWork(n);
                }}
                placeholder="From"
                className="flex-1 rounded-btn border border-border-card bg-white px-2 py-1 text-xs"
              />
              <input
                value={w.to}
                onChange={(ev) => {
                  const n = [...work];
                  n[i] = { ...w, to: ev.target.value };
                  setWork(n);
                }}
                placeholder="To"
                className="flex-1 rounded-btn border border-border-card bg-white px-2 py-1 text-xs"
              />
              <button
                type="button"
                onClick={() => setWork(work.filter((_, j) => j !== i))}
                className="text-xs text-error-text"
              >
                x
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setWork([
              ...work,
              { company: "", title: "", from: "", to: "", description: "" },
            ])
          }
          className="text-xs font-medium text-rausch"
        >
          + Add work experience
        </button>
      </SectionCard>
      <div className="flex gap-2">
        <button
          onClick={() =>
            onSave({ education_history: edu, work_experience: work })
          }
          disabled={saving}
          className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white hover:bg-rausch disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save"}
        </button>
        <button
          onClick={onCancel}
          className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary hover:text-near-black"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
